/* ═══════════════════════════════════════════════════════
   src/content/domains/cards/sage.tsx
   All Sage domain cards with text pre-rendered as JSX.
   Edit `rendered` to update display. Edit `text` to update search.
   ═══════════════════════════════════════════════════════ */

import { DomainCard } from './types';
import {
  HOPE,
  STRESS,
  SPELLCAST_ROLL,
  VERY_CLOSE,
  CLOSE,
  FAR,
  MELEE,
  VULNERABLE,
  LOADOUT,
  HIT_POINTS,
  HIT_POINT,
  ARMOR_SLOT,
  LONG_REST,
} from '../../templates';

const sageCards: DomainCard[] = [
  {
    name: "Gifted Tracker",
    level: 1,
    domain: "Sage",
    type: "Ability",
    recallCost: 0,
    text: "When you're tracking a specific creature or group of creatures based on signs of their passage, you can **spend any number of Hope** and ask the GM that many questions from the following list.\n\n- What direction did they go?\n- How long ago did they pass through?\n- What were they doing in this location?\n- How many of them were here?\n\nWhen you encounter creatures you've tracked in this way, gain a +1 bonus to your Evasion against them.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you're tracking a specific creature or group of creatures based on signs of their passage, you can <strong className="inline-strong">spend any number of {HOPE}</strong> and ask the GM that many questions from the following list.</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>What direction did they go?</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>How long ago did they pass through?</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>What were they doing in this location?</span></div>
        <div key={5} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>How many of them were here?</span></div>
        <div key={6} className="card-text-gap" />
        <p key={7} className="card-text-para">When you encounter creatures you've tracked in this way, gain a +1 bonus to your Evasion against them.</p>
      </>
    ),
  },
  {
    name: "Nature's Tongue",
    level: 1,
    domain: "Sage",
    type: "Ability",
    recallCost: 0,
    text: "You can speak the language of the natural world. When you want to speak to the plants and animals around you, make an **Instinct Roll (12)**. On a success, they'll give you the information they know. On a roll with Fear, their knowledge might be limited or come at a cost.\n\nAdditionally, before you make a Spellcast Roll while within a natural environment, you can **spend a Hope** to gain a +2 bonus to the roll.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You can speak the language of the natural world. When you want to speak to the plants and animals around you, make an <strong className="inline-strong">Instinct Roll (12)</strong>. On a success, they'll give you the information they know. On a roll with Fear, their knowledge might be limited or come at a cost.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Additionally, before you make a {SPELLCAST_ROLL} while within a natural environment, you can <strong className="inline-strong">spend a {HOPE}</strong> to gain a +2 bonus to the roll.</p>
      </>
    ),
  },
  {
    name: "Vicious Entangle",
    level: 1,
    domain: "Sage",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll** against a target within Far range. On a success, roots and vines reach out from the ground, dealing **1d8+1** physical damage and temporarily _Restraining_ the target.\n\nAdditionally on a success, you can **spend a Hope** to temporarily _Restrain_ another adversary within Very Close range of your target.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against a target within {FAR} range. On a success, roots and vines reach out from the ground, dealing <strong className="inline-strong">1d8+1</strong> physical damage and temporarily <em>Restraining</em> the target.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Additionally on a success, you can <strong className="inline-strong">spend a {HOPE}</strong> to temporarily <em>Restrain</em> another adversary within {VERY_CLOSE} range of your target.</p>
      </>
    ),
  },
  {
    name: "Conjure Swarm",
    level: 2,
    domain: "Sage",
    type: "Spell",
    recallCost: 1,
    text: "_Tekaira Armored Beetles:_ **Mark a Stress** to conjure armored beetles that encircle you. When you next take damage, reduce the severity by one threshold. You can **spend a Hope** to keep the beetles conjured after taking damage.\n\n_Fire Flies:_ Make a **Spellcast Roll** against all adversaries within Close range. **Spend a Hope** to deal **2d8+3** magic damage to targets you succeeded against.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><em>Tekaira Armored Beetles:</em> <strong className="inline-strong">Mark a {STRESS}</strong> to conjure armored beetles that encircle you. When you next take damage, reduce the severity by one threshold. You can <strong className="inline-strong">spend a {HOPE}</strong> to keep the beetles conjured after taking damage.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para"><em>Fire Flies:</em> Make a {SPELLCAST_ROLL} against all adversaries within {CLOSE} range. <strong className="inline-strong">Spend a {HOPE}</strong> to deal <strong className="inline-strong">2d8+3</strong> magic damage to targets you succeeded against.</p>
      </>
    ),
  },
  {
    name: "Natural Familiar",
    level: 2,
    domain: "Sage",
    type: "Spell",
    recallCost: 1,
    text: "**Spend a Hope** to summon a small nature spirit or forest critter to your side until your next rest, you cast Natural Familiar again, or the familiar is targeted by an attack. If you **spend an additional Hope**, you can summon a familiar that flies. You can communicate with them, make a **Spellcast Roll** to command them to perform simple tasks, and **mark a Stress** to see through their eyes.\n\nWhen you deal damage to an adversary within Melee range of your familiar, you add a **d6** to your damage roll.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend a {HOPE}</strong> to summon a small nature spirit or forest critter to your side until your next rest, you cast Natural Familiar again, or the familiar is targeted by an attack. If you <strong className="inline-strong">spend an additional {HOPE}</strong>, you can summon a familiar that flies. You can communicate with them, make a {SPELLCAST_ROLL} to command them to perform simple tasks, and <strong className="inline-strong">mark a {STRESS}</strong> to see through their eyes.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">When you deal damage to an adversary within {MELEE} range of your familiar, you add a <strong className="inline-strong">d6</strong> to your damage roll.</p>
      </>
    ),
  },
  {
    name: "Corrosive Projectile",
    level: 3,
    domain: "Sage",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll** against a target within Far range. On a success, deal **d6+4** magic damage using your Proficiency. Additionally, **mark 2 or more Stress** to make them permanently _Corroded_. While a target is _Corroded_, they gain a -1 penalty to their Difficulty for every 2 Stress you spent. This condition can stack.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against a target within {FAR} range. On a success, deal <strong className="inline-strong">d6+4</strong> magic damage using your Proficiency. Additionally, <strong className="inline-strong">mark 2 or more {STRESS}</strong> to make them permanently <em>Corroded</em>. While a target is <em>Corroded</em>, they gain a -1 penalty to their Difficulty for every 2 {STRESS} you spent. This condition can stack.</p>
      </>
    ),
  },
  {
    name: "Towering Stalk",
    level: 3,
    domain: "Sage",
    type: "Spell",
    recallCost: 1,
    text: "Once per rest, you can conjure a thick, twisting stalk within Close range that can be easily climbed. Its height can grow up to Far range.\n\n**Mark a Stress** to use this spell as an attack. Make a **Spellcast Roll** against an adversary or group of adversaries within Close range. The erupting stalk lifts targets you succeed against into the air and drops them, dealing **d8** physical damage using your Proficiency.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, you can conjure a thick, twisting stalk within {CLOSE} range that can be easily climbed. Its height can grow up to {FAR} range.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para"><strong className="inline-strong">Mark a {STRESS}</strong> to use this spell as an attack. Make a {SPELLCAST_ROLL} against an adversary or group of adversaries within {CLOSE} range. The erupting stalk lifts targets you succeed against into the air and drops them, dealing <strong className="inline-strong">d8</strong> physical damage using your Proficiency.</p>
      </>
    ),
  },
  {
    name: "Death Grip",
    level: 4,
    domain: "Sage",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll** against a target within Close range and choose one of the following options:\n\n- You pull the target into Melee range or pull yourself into Melee range of them.\n- You constrict the target and force them to mark 2 Stress.\n- All adversaries between you and the target must succeed on a Reaction Roll (13) or be hit by vines, taking **3d6+2** physical damage.\n\nOn a success, vines reach out from your hands, causing the chosen effect and temporarily _Restraining_ the target.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against a target within {CLOSE} range and choose one of the following options:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>You pull the target into {MELEE} range or pull yourself into {MELEE} range of them.</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>You constrict the target and force them to mark 2 {STRESS}.</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>All adversaries between you and the target must succeed on a Reaction Roll (13) or be hit by vines, taking <strong className="inline-strong">3d6+2</strong> physical damage.</span></div>
        <div key={5} className="card-text-gap" />
        <p key={6} className="card-text-para">On a success, vines reach out from your hands, causing the chosen effect and temporarily <em>Restraining</em> the target.</p>
      </>
    ),
  },
  {
    name: "Healing Field",
    level: 4,
    domain: "Sage",
    type: "Spell",
    recallCost: 2,
    text: "Once per long rest, you can conjure a field of healing plants around you. Everywhere within Close range of you bursts to life with vibrant nature, allowing you and all allies in the area to clear a Hit Point.\n\n**Spend 2 Hope** to allow you and all allies to clear 2 Hit Points instead.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST}, you can conjure a field of healing plants around you. Everywhere within {CLOSE} range of you bursts to life with vibrant nature, allowing you and all allies in the area to clear a {HIT_POINT}.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para"><strong className="inline-strong">Spend 2 {HOPE}</strong> to allow you and all allies to clear 2 {HIT_POINTS} instead.</p>
      </>
    ),
  },
  {
    name: "Thorn Skin",
    level: 5,
    domain: "Sage",
    type: "Spell",
    recallCost: 1,
    text: "Once per rest, **spend a Hope** to sprout thorns all over your body. When you do, place a number of tokens equal to your Spellcast trait on this card. When you take damage, you can spend any number of tokens to roll that number of **d6s**. Add the results together and reduce the incoming damage by that amount. If you're within Melee range of the attacker, deal that amount of damage back to them.\n\nWhen you take a rest, clear all unspent tokens.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, <strong className="inline-strong">spend a {HOPE}</strong> to sprout thorns all over your body. When you do, place a number of tokens equal to your Spellcast trait on this card. When you take damage, you can spend any number of tokens to roll that number of <strong className="inline-strong">d6s</strong>. Add the results together and reduce the incoming damage by that amount. If you're within {MELEE} range of the attacker, deal that amount of damage back to them.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">When you take a rest, clear all unspent tokens.</p>
      </>
    ),
  },
  {
    name: "Wild Fortress",
    level: 5,
    domain: "Sage",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll (13)**. On a success, **spend 2 Hope** to grow a natural barricade in the shape of a dome that you and one ally can take cover within. While inside the dome, a creature can't be targeted by attacks and can't make attacks. Attacks made against the dome automatically succeed. The dome has the following damage thresholds and lasts until it marks 3 Hit Points. Place tokens on this card to represent marking Hit Points.\n\nThresholds: 15/30",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (13). On a success, <strong className="inline-strong">spend 2 {HOPE}</strong> to grow a natural barricade in the shape of a dome that you and one ally can take cover within. While inside the dome, a creature can't be targeted by attacks and can't make attacks. Attacks made against the dome automatically succeed. The dome has the following damage thresholds and lasts until it marks 3 {HIT_POINTS}. Place tokens on this card to represent marking {HIT_POINTS}.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Thresholds: 15/30</p>
      </>
    ),
  },
  {
    name: "Conjured Steeds",
    level: 6,
    domain: "Sage",
    type: "Spell",
    recallCost: 0,
    text: "**Spend any number of Hope** to conjure that many magical steeds (such as horses, camels, or elephants) that you and your allies can ride until your next long rest or the steeds take any damage. The steeds double your land speed while traveling and, when in danger, allow you to move within Far range without having to roll. Creatures riding a steed gain a -2 penalty to attack rolls and a +2 bonus to damage rolls.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend any number of {HOPE}</strong> to conjure that many magical steeds (such as horses, camels, or elephants) that you and your allies can ride until your next {LONG_REST} or the steeds take any damage. The steeds double your land speed while traveling and, when in danger, allow you to move within {FAR} range without having to roll. Creatures riding a steed gain a -2 penalty to attack rolls and a +2 bonus to damage rolls.</p>
      </>
    ),
  },
  {
    name: "Forager",
    level: 6,
    domain: "Sage",
    type: "Ability",
    recallCost: 1,
    text: "As an additional downtime move you can choose, roll a d6 to see what you forage. Work with the GM to describe it and add it to your inventory as a consumable. Your party can carry up to five foraged consumables at a time.\n\n- 1. A unique food (Clear 2 Stress)\n- 2. A beautiful relic (Gain 2 Hope)\n- 3. An arcane rune (+2 to a Spellcast Roll)\n- 4. A healing vial (Clear 2 Hit Points)\n- 5. A luck charm (Reroll any die)\n- 6. Choose one of the options above.",
    rendered: (
      <>
        <p key={0} className="card-text-para">As an additional downtime move you can choose, roll a d6 to see what you forage. Work with the GM to describe it and add it to your inventory as a consumable. Your party can carry up to five foraged consumables at a time.</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>1. A unique food (Clear 2 Stress)</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>2. A beautiful relic (Gain 2 Hope)</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>3. An arcane rune (+2 to a Spellcast Roll)</span></div>
        <div key={5} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>4. A healing vial (Clear 2 Hit Points)</span></div>
        <div key={6} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>5. A luck charm (Reroll any die)</span></div>
        <div key={7} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>6. Choose one of the options above.</span></div>
      </>
    ),
  },
  {
    name: "Sage-Touched",
    level: 7,
    domain: "Sage",
    type: "Ability",
    recallCost: 2,
    text: "When 4 or more of the domain cards in your loadout are from the Sage domain, gain the following benefits:\n\n- While you're in a natural environment, you gain a +2 bonus to your Spellcast Rolls.\n- Once per rest, you can double your Agility or Instinct when making a roll that uses that trait. You must choose to do this before you roll.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When 4 or more of the domain cards in your {LOADOUT} are from the Sage domain, gain the following benefits:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>While you're in a natural environment, you gain a +2 bonus to your {SPELLCAST_ROLL}s.</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Once per rest, you can double your Agility or Instinct when making a roll that uses that trait. You must choose to do this before you roll.</span></div>
      </>
    ),
  },
  {
    name: "Wild Surge",
    level: 7,
    domain: "Sage",
    type: "Spell",
    recallCost: 2,
    text: "Once per long rest, **mark a Stress** to channel the natural world around you and enhance yourself. Describe how your appearance changes, then place a **d6** on this card with the 1 value facing up.\n\nWhile the Wild Surge Die is active, you add its value to every action roll you make. After you add its value to a roll, increase the Wild Surge Die's value by one. When the die's value would exceed 6 or you take a rest, this form drops and you must **mark an additional Stress**.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST}, <strong className="inline-strong">mark a {STRESS}</strong> to channel the natural world around you and enhance yourself. Describe how your appearance changes, then place a <strong className="inline-strong">d6</strong> on this card with the 1 value facing up.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">While the Wild Surge Die is active, you add its value to every action roll you make. After you add its value to a roll, increase the Wild Surge Die's value by one. When the die's value would exceed 6 or you take a rest, this form drops and you must <strong className="inline-strong">mark an additional {STRESS}</strong>.</p>
      </>
    ),
  },
  {
    name: "Forest Sprites",
    level: 8,
    domain: "Sage",
    type: "Spell",
    recallCost: 2,
    text: "Make a **Spellcast Roll (13)**. On a success, **spend any number of Hope** to create an equal number of small forest sprites who appear at points you choose within Far range, providing the following benefits:\n\n- Your allies gain a +3 bonus to attack rolls against adversaries within Melee range of a sprite.\n- An ally who marks an Armor Slot while within Melee range of a sprite can mark an additional Armor Slot.\n\nA sprite vanishes after granting a benefit or taking any damage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (13). On a success, <strong className="inline-strong">spend any number of {HOPE}</strong> to create an equal number of small forest sprites who appear at points you choose within {FAR} range, providing the following benefits:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Your allies gain a +3 bonus to attack rolls against adversaries within {MELEE} range of a sprite.</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>An ally who marks an {ARMOR_SLOT} while within {MELEE} range of a sprite can mark an additional {ARMOR_SLOT}.</span></div>
        <div key={4} className="card-text-gap" />
        <p key={5} className="card-text-para">A sprite vanishes after granting a benefit or taking any damage.</p>
      </>
    ),
  },
  {
    name: "Rejuvenation Barrier",
    level: 8,
    domain: "Sage",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll (15)**. Once per rest on a success, create a temporary barrier of protective energy around you at Very Close range. You and all allies within the barrier when this spell is cast clear **1d4** Hit Points. While the barrier is up, you and all allies within have resistance to physical damage from outside the barrier.\n\nWhen you move, the barrier follows you.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (15). Once per rest on a success, create a temporary barrier of protective energy around you at {VERY_CLOSE} range. You and all allies within the barrier when this spell is cast clear <strong className="inline-strong">1d4</strong> {HIT_POINTS}. While the barrier is up, you and all allies within have resistance to physical damage from outside the barrier.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">When you move, the barrier follows you.</p>
      </>
    ),
  },
  {
    name: "Fane of the Wilds",
    level: 9,
    domain: "Sage",
    type: "Ability",
    recallCost: 2,
    text: "After a long rest, place a number of tokens equal to the number of Sage domain cards in your loadout and vault on this card.\n\nWhen you would make a Spellcast Roll, you can spend any number of tokens after the roll to gain a +1 bonus for each token spent.\n\nWhen you critically succeed on a Spellcast Roll for a Sage domain spell, gain a token.\n\nWhen you take a long rest, clear all unspent tokens.",
    rendered: (
      <>
        <p key={0} className="card-text-para">After a long rest, place a number of tokens equal to the number of Sage domain cards in your loadout and vault on this card.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">When you would make a Spellcast Roll, you can spend any number of tokens after the roll to gain a +1 bonus for each token spent.</p>
        <div key={3} className="card-text-gap" />
        <p key={4} className="card-text-para">When you critically succeed on a Spellcast Roll for a Sage domain spell, gain a token.</p>
        <div key={5} className="card-text-gap" />
        <p key={6} className="card-text-para">When you take a long rest, clear all unspent tokens.</p>
      </>
    ),
  },
  {
    name: "Plant Dominion",
    level: 9,
    domain: "Sage",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll (18)**. Once per long rest on a success, you reshape the natural world, changing the surrounding plant life anywhere within Far range of you. For example, you can grow trees instantly, clear a path through dense vines, or create a wall of roots.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (18). Once per {LONG_REST} on a success, you reshape the natural world, changing the surrounding plant life anywhere within {FAR} range of you. For example, you can grow trees instantly, clear a path through dense vines, or create a wall of roots.</p>
      </>
    ),
  },
  {
    name: "Force of Nature",
    level: 10,
    domain: "Sage",
    type: "Spell",
    recallCost: 2,
    text: "**Mark a Stress** to transform into a hulking nature spirit, gaining the following benefits:\n\n- When you succeed on an attack or Spellcast Roll, gain a +10 bonus to the damage roll.\n- When you deal enough damage to defeat a creature within Close range, you absorb them and clear an Armor Slot.\n- You can't be _Restrained_.\n\nBefore you make an action roll, you must **spend a Hope**. If you can't, you revert to your normal form.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Mark a {STRESS}</strong> to transform into a hulking nature spirit, gaining the following benefits:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>When you succeed on an attack or {SPELLCAST_ROLL}, gain a +10 bonus to the damage roll.</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>When you deal enough damage to defeat a creature within {CLOSE} range, you absorb them and clear an {ARMOR_SLOT}.</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>You can't be <em>Restrained</em>.</span></div>
        <div key={5} className="card-text-gap" />
        <p key={6} className="card-text-para">Before you make an action roll, you must <strong className="inline-strong">spend a {HOPE}</strong>. If you can't, you revert to your normal form.</p>
      </>
    ),
  },
  {
    name: "Tempest",
    level: 10,
    domain: "Sage",
    type: "Spell",
    recallCost: 2,
    text: "Choose one of the following tempests and make a **Spellcast Roll** against all targets within Far range. Targets you succeed against experience its effects until the GM spends a Fear on their turn to end this spell.\n\n- _Blizzard_: Deal **2d20+8** magic damage and targets are temporarily _Vulnerable_.\n- _Hurricane_: Deal **3d10+10** magic damage and choose a direction the wind is blowing. Targets can't move against the wind.\n- _Sandstorm_: Deal **5d6+9** magic damage. Attacks made from beyond Melee range have disadvantage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Choose one of the following tempests and make a {SPELLCAST_ROLL} against all targets within {FAR} range. Targets you succeed against experience its effects until the GM spends a Fear on their turn to end this spell.</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span><em>Blizzard</em>: Deal <strong className="inline-strong">2d20+8</strong> magic damage and targets are temporarily {VULNERABLE}.</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span><em>Hurricane</em>: Deal <strong className="inline-strong">3d10+10</strong> magic damage and choose a direction the wind is blowing. Targets can't move against the wind.</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span><em>Sandstorm</em>: Deal <strong className="inline-strong">5d6+9</strong> magic damage. Attacks made from beyond {MELEE} range have disadvantage.</span></div>
      </>
    ),
  },
];

export default sageCards;
